import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import TeamDelegate from './team.delegate';

class TeamController extends Inherit(ClockoutController, TeamDelegate) {
    state = {
        user: {},
        loading: true,
        users: [],
        filteredUsers: [],
        searchQuery: '',
        showAddMemberModal: false,
        newMemberEmail: '',
        newMemberRole: 'Member',
        usersLoading: false
    };

    async initialize() {
        // Check if user has valid data
        if (!this.user || !this.user.access || this.user.access.length === 0) {
            this.navigateLogin();
            return;
        }

        this.setState({ user: this.data.user, loading: false });
    }

    componentDidUpdate(prevProps, prevState) {
        // Load users when user data is loaded
        if (prevState.user?.fi !== this.state.user?.fi && this.state.user?.fi) {
            this.loadUsers();
        }

        // Filter users when search query or users change
        if (prevState.searchQuery !== this.state.searchQuery || prevState.users !== this.state.users) {
            this.filterUsers();
        }
    }

    async loadUsers() {
        try {
            this.setState({ usersLoading: true });
            const users = await this.data.fetchAdminUsers();
            this.setState({ users: users?.users || [], usersLoading: false });
        } catch (error) {
            console.error('Error loading users:', error);
            this.setState({ usersLoading: false });
        }
    }

    filterUsers() {
        const { users, searchQuery } = this.state;
        
        if (!searchQuery || searchQuery.trim() === '') {
            this.setState({ filteredUsers: users });
            return;
        }

        const query = searchQuery.toLowerCase().trim();
        const filtered = users.filter(user => {
            const name = (user.name || '').toLowerCase();
            const email = (user.email || '').toLowerCase();
            const role = this.getUserRole(user).toLowerCase();
            
            return name.includes(query) || email.includes(query) || role.includes(query);
        });

        this.setState({ filteredUsers: filtered });
    }

    handleSearchChange(e) {
        this.setSearchQuery(e.target.value);
    }

    handleOpenAddMemberModal() {
        this.setShowAddMemberModal(true);
        this.setNewMemberEmail('');
        this.setNewMemberRole('Member');
    }

    handleCloseAddMemberModal() {
        this.setShowAddMemberModal(false);
        this.setNewMemberEmail('');
        this.setNewMemberRole('Member');
    }

    async handleInviteMember() {
        const { newMemberEmail, newMemberRole } = this.state;
        
        if (!newMemberEmail || !newMemberEmail.includes('@')) {
            alert('Please enter a valid email address');
            return;
        }

        try {
            this.setState({ usersLoading: true });
            const result = await this.data.sendInvitation({ 
                email: newMemberEmail,
                role: newMemberRole 
            });
            
            if (result && result.success) {
                this.handleCloseAddMemberModal();
                await this.loadUsers();
            } else {
                alert(result?.message || 'Failed to send invitation. Please try again.');
            }
        } catch (error) {
            console.error('Error sending invitation:', error);
            alert('An error occurred while sending the invitation. Please try again.');
        } finally {
            this.setState({ usersLoading: false });
        }
    }

    async handleDeleteUser(userId) {
        if (!window.confirm('Are you sure you want to remove this user\'s access?')) {
            return;
        }

        try {
            await this.data.removeUserAccess(userId);
            await this.loadUsers();
        } catch (error) {
            console.error('Error removing user access:', error);
            alert('Failed to remove user access. Please try again.');
        }
    }

    async handleSendPasswordReset(email) {
        if (!window.confirm(`Send password reset email to ${email}?`)) {
            return;
        }

        try {
            await this.data.sendPasswordReset(email);
            alert(`Password reset email sent to ${email}`);
        } catch (error) {
            console.error('Error sending password reset:', error);
            alert('Failed to send password reset email. Please try again.');
        }
    }

    getUserRole(user) {
        // Determine role based on user data
        // This logic may need to be adjusted based on your data structure
        if (user.role) return user.role;
        if (user.isOwner) return 'Owner';
        if (user.isAdmin) return 'Admin';
        return 'Member';
    }

    getUserStatus(user) {
        return user.active ? 'Active' : 'Inactive';
    }
}

export default TeamController;

